import { useNavigate } from 'react-router';
import { useTrainees } from '@t90/api-client';
import { DataTable, Button, Badge, Plus, Avatar, AvatarFallback, AvatarImage } from '@t90/ui';
import { formatDate } from '@t90/common';
import type { ColumnDef } from '@tanstack/react-table';
import type { Trainee } from '@t90/common';

const columns: ColumnDef<Trainee>[] = [
  {
    id: 'trainee',
    header: 'Trainee',
    cell: ({ row }) => {
      const trainee = row.original;
      const initials = `${trainee.first_name?.[0] ?? ''}${trainee.last_name?.[0] ?? ''}`;
      return (
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8">
            <AvatarImage src={trainee.avatar_url ?? undefined} />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">{trainee.first_name} {trainee.last_name}</p>
            <p className="text-xs text-muted-foreground">{trainee.email}</p>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.getValue('status') as string;
      return <Badge variant={getStatusVariant(status)}>{status}</Badge>;
    },
  },
  {
    accessorKey: 'location_id',
    header: 'Location',
    cell: ({ row }) => {
      const id = row.getValue('location_id') as string | null;
      return id ? id.substring(0, 8) + '...' : '-';
    },
  },
  {
    accessorKey: 'cohort',
    header: 'Cohort',
  },
  {
    accessorKey: 'start_date',
    header: 'Start Date',
    cell: ({ row }) => {
      const date = row.getValue('start_date') as string | null;
      return date ? formatDate(date) : '-';
    },
  },
  {
    accessorKey: 'graduation_target_date',
    header: 'End Date',
    cell: ({ row }) => {
      const date = row.getValue('graduation_target_date') as string | null;
      return date ? formatDate(date) : '-';
    },
  },
];

function getStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Graduated':
      return 'default';
    case 'On Leave':
      return 'warning';
    case 'Terminated':
      return 'destructive';
    case 'Pending':
      return 'secondary';
    default:
      return 'secondary';
  }
}

export function TraineesPage() {
  const navigate = useNavigate();
  const { data: trainees, isLoading } = useTrainees();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Trainees</h1>
          <p className="text-muted-foreground">
            Manage trainee enrollment and track program progress
          </p>
        </div>
        <Button onClick={() => navigate('/trainees/new')}>
          <Plus className="mr-2 h-4 w-4" />
          Add Trainee
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={trainees ?? []}
        searchKey="first_name"
        searchPlaceholder="Search trainees..."
        isLoading={isLoading}
        onRowClick={(row) => navigate(`/trainees/${row.id}`)}
        emptyMessage="No trainees found."
      />
    </div>
  );
}
